import React, { FC, useEffect, useRef } from 'react';

import { CloudFeatureStatus } from '@ariksa/cloud-account/api';
import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { colorOpacity, customTheme } from 'theme';

import { actions } from 'app/containers/PolicyHub/Blueprints/slice';
import {
  CustomTooltip,
  renderCloudIcon,
  StackedCell,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import {
  CheckmarkCircleOutlineIcon,
  CloseCircleOutlineIcon,
  IconTypes,
} from 'components/Icons';
import { usePageContentContext } from 'components/Layout';
import { Drawer } from 'components/Overlay';
import { DashboardWidgetTooltipIcon } from 'containers/Dashboard/utils/utils';
import { renderBusinessCritical } from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ManagedAccounts: FC<Props> = props => {
  const { isOpen, onClose } = props;
  const { blueprint, managedAccounts } = useSelector(selectBlueprints);
  const dispatch = useDispatch();

  const ref = useRef(document.querySelector('.portal-container'));
  const { accountMapping } = useCloudAccountId();
  const { contentHeight } = usePageContentContext();

  useEffect(() => {
    dispatch(
      actions.getManagedAccounts({
        q: {
          blueprintId: blueprint.data?.id,
          page: managedAccounts.page.info.page_number,
          size: managedAccounts.page.info.page_size,
        },
      }),
    );
  }, [blueprint.data, managedAccounts.page.info, dispatch]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Account</Box>,
      render: ({ row }) => {
        const account = accountMapping?.[row?.account_id];
        return (
          <WithResourceIcon
            resourceType={IconTypes.Account}
            color={customTheme.colors.gray['300']}
            bgColor={colorOpacity(customTheme.colors.gray['300'], 0.2)}
            useCustomColor
          >
            <StackedCell
              upper={account?.name}
              lower={account?.cloud_account_id}
            />
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Cloud',
      render: ({ row }) => {
        const account = accountMapping?.[row?.account_id];
        return (
          <Center>{renderCloudIcon({ provider: account?.cloud_type })}</Center>
        );
      },
    },
    {
      header: 'No. of Entities',
      accessor: 'resource_count',
      //align: 'right',
      render: ({ value }) => formatNumber(value),
    },
    {
      header: 'Environment',
      accessor: 'environment_name',
      align: 'left',
    },
    {
      header: 'Business Critical',
      accessor: 'is_business_critical',
      render: ({ value }) => renderBusinessCritical(value),
    },
    {
      header: 'Remediation',
      accessor: 'account_id',
      render: ({ value }) => {
        const account = accountMapping?.[value];
        const flag = account?.remediation_status === CloudFeatureStatus.Success;
        return (
          <Center>
            <CustomTooltip
              label={'Remediation ' + (flag ? '' : 'not ') + 'enabled'}
              w={32}
              textAlign="center"
            >
              <Center boxSize={5} color="primary">
                {flag ? (
                  <CheckmarkCircleOutlineIcon />
                ) : (
                  <CloseCircleOutlineIcon />
                )}
              </Center>
            </CustomTooltip>
          </Center>
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <StackedHeader
            upper="Managed Accounts"
            lower={blueprint.data?.name}
            type={IconTypes.Account}
          />
        }
        styles={{
          content: { maxW: '1400px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
        closeButton
        body={
          <Stack w="full" h={contentHeight + 'px'} pt={3} spacing={5}>
            <HStack>
              <DashboardWidgetTooltipIcon />
              <Box color="primary">
                To enforce policies on an environment, choose “Enforce” from the
                policy blueprint
              </Box>
            </HStack>
            <Box h="full">
              <Table
                data={managedAccounts.data}
                isLoading={managedAccounts.isLoading}
                columns={columns}
                cursor="pointer"
                /*onRowClick={row => {
                  dispatch(
                    actions.setAccount({
                      type: EnvironmentTypes.Environment,
                      environmentId: row?.id,
                      environment: row,
                    }),
                  );
                  navigate('/compliance');
                }}*/
              />
            </Box>
          </Stack>
        }
      />
    </>
  );
};
